import React, { useEffect, useMemo, useState } from 'react';
import { Icon, Table, Header, Button, Input, Modal, useFrontier, Stat } from 'react-frontier2';
import { addCommas, bindClick } from '@arema/components/Util';
import { useUser } from '../AdminHooks';
import API from '../API';
import Validator from "@arema/components/Validator";
import classNames from 'classnames';
import UserAccess from '../UserAccess';

interface SectionHoldModalProps {
	dateId: number,
	sectionId: number,
	open: boolean,
	onClose: ()=>void,
	title?: string,
	infinite?: boolean,
}

interface SectionHold {
	hold_id?: any,
	date_id: number,
	section_id: number,
	amount: string,
	comments: string,
	user_creator: number,
	user_creator_name: string,
}

interface TicketsInfo {
	available: number,
	ticket_count: number,
	tickets_sold: number,
	blocked: number,
}

function SectionHoldModal(props: SectionHoldModalProps){
	var { hasAccess, user } = useUser();
	var { Toast } = useFrontier();
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [holders, setHolders] = useState<SectionHold[]>(null);
	var [selectedHold, setSelectedHold] = useState<number>(null);
	var [holderAux, setHolderAux] = useState<SectionHold>(null);
	var [ticketInfo, setTicketInfo] = useState<TicketsInfo>(null);

	useEffect(()=>{
		if(!props.open) return;
		if(holders) setHolders(null);
		setLoadError(null);
		setLoading(false);
		getHoldData();
	}, [props.open, props.sectionId, props.dateId]);

	const getHoldData = ()=>{
		API.getDateSectionHolders(props.dateId, props.sectionId).then(res=>{
			if(res.error) return setLoadError(res.message);
			console.log(res.data);
			setHolders(res.data.holders);
			setTicketInfo({
				blocked: (res.data.available?.tickets_hold || 0),
				...res.data.available
			});
		}).catch(err=>{
			console.log(err);
			return setLoadError('Hubo un error inesperado cargando las butacas reservadas (LCL-1)');
		})
	}

	const onNewHolder = ()=>{
		setHolderAux({
			hold_id: null,
			date_id: props.dateId,
			section_id: props.sectionId,
			amount: '',
			comments: '',
			user_creator: user.user_id,
			user_creator_name: user.name,
		});
		setSelectedHold(-1);
	}

	const onFormChange = (key: keyof SectionHold)=>{
		return (val: any)=>{
			if(selectedHold===null) return;
			if(selectedHold===-1){
				var hold: any = { ...holderAux };
				hold[key] = val;
				setHolderAux(hold);
			}else{
				var new_holders: any[] = [...holders];
				var ix = new_holders.findIndex(a=>a.hold_id===selectedHold);
				if(ix===-1) return;
				new_holders[ix][key] = val;
				setHolders(new_holders);
			}
		}
	}

	const onClickEditRow = (hold_id: number)=>{
		return ()=>{
			if(!hasAccess(UserAccess.DATES.ADD_SECTION_HOLD)) return Toast.error('No tienes acceso a realizar esta acción.');
			if(selectedHold !== hold_id){
				var hold = holders.find(a=>a.hold_id===hold_id);
				if(!hold) return;
				setHolderAux({ ...hold });
				setSelectedHold(hold_id);
			} else {
				Toast.error('Terminar la reservacion seleccionada');
			}
		}
	}

	const createTicketHold = (hold: SectionHold)=>{
		if(!hasAccess(UserAccess.DATES.ADD_SECTION_HOLD)) return Toast.error('No tienes acceso a realizar esta acción.');
		API.createSectionHold(props.dateId, props.sectionId, parseInt(hold.amount), hold.comments).then(res=>{
			if(res.error) return Toast.error(res.message);
			setSelectedHold(null);
			hold.hold_id = res.data.hold_id
			setHolders([
				...holders,
				hold,
			])
			getHoldData();
			Toast.success('Se creado la reserva de boletos correctamente.');
		}).catch(err=>{
			return Toast.error('Hubo un error inesperado guardando las boletos reservados (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	const updateTicketHold = (holderSave: SectionHold)=>{
		if(!hasAccess(UserAccess.DATES.ADD_SECTION_HOLD)) return Toast.error('No tienes acceso a realizar esta acción.');
		API.updateSectionHold(holderSave).then(res=>{
			if(res.error) return Toast.error(res.message);
			setSelectedHold(null);
			setHolders([
				...holders.filter(a=>a.hold_id!==holderSave.hold_id),
				holderSave
			]);

			getHoldData();
			Toast.success('Se actualizo correctamente la reserva de boletos.');
		}).catch(err=>{
			return Toast.error('Hubo un error inesperado actualizando las boletos reservados (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	const onSaveRow = (hold_id: number)=>{
		return ()=>{
			if(!hasAccess(UserAccess.DATES.ADD_SECTION_HOLD)) return Toast.error('No tienes acceso a realizar esta acción.');
			// cantidad maxima de tickets a reservar
			var maxHolds = ticketInfo.ticket_count-ticketInfo.tickets_sold;
			if(maxHolds===0) return Toast.error('No hay boletos disponibles para bloquear');

			var hold : SectionHold = hold_id===-1 ? holderAux : holders.find(a=>a.hold_id===hold_id);
			if(!hold) return;
			
			var { valid, prompts } = Validator(hold, {
				amount: [
					{ rule: 'number', label: 'Cantidad' },
					{ rule: 'min', params: [1], prompt: 'La cantidad minima para bloquear es 1.' },
					{ rule: 'max', params: [maxHolds], prompt: `La cantidad maxima a bloquear es ${maxHolds}.` },
				],
				comments: [{ rule: 'length', params: [3, 128], label: 'Comentario' }],
			});
			
			if(!valid){
				return Toast.error(prompts[0]);
			}

			// validar si hubo un cambio y si no cambio ningun dato no enviar la informacion
			if(hold_id!==-1 && holderAux && holderAux.comments === hold.comments && holderAux.amount === hold.amount){
				setSelectedHold(null);
				return;
			}

			setLoading(true);
			if(hold.hold_id===null){
				createTicketHold(hold);
			} else {
				updateTicketHold(hold);
			}
		}
	}

	const onDeleteRow = (hold_id: number)=>{
		return ()=>{
			if(!hasAccess(UserAccess.DATES.DELETE_SECTION_HOLD)) return Toast.error('No tienes acceso a realizar esta acción.');
			setSelectedHold(hold_id);
			if(hold_id && hold_id > 0){
				setLoading(true);
				API.deleteSectionHold(hold_id).then(res=>{
					if(res.error) return Toast.error(res.message);
					setHolders([...holders].filter(a=>a.hold_id!==hold_id));
					if(res.data){
						getHoldData();
						Toast.success('Se elimino el bloqueo correctamente');
					}
				}).catch(err=>{
					Toast.error('Hubo un error inesperado eliminando las butacas reservadas(LCL-1)');
				}).finally(()=>{
					setLoading(false);
					setSelectedHold(null);
				});
			} else {
				// eliminar del array si no fue guardado previamente
				setHolders(holders.filter(a=>a.hold_id!==hold_id));
				setHolderAux(null);
				setSelectedHold(null);
			}
		}
	}

	var {
		tickets_blocked,
		tickets_available,
		editing_hold
	} = useMemo(()=>{
		var tickets_blocked = holders ? holders.filter(a=>!!a.hold_id).reduce((a,b)=>a+(parseInt(b.amount) || 0), 0) : 0;
		if(selectedHold===-1 && holderAux){
			tickets_blocked += (parseInt(holderAux.amount) || 0);
		}
		var tickets_available = (ticketInfo && ticketInfo.ticket_count > 0) ? ticketInfo.ticket_count-ticketInfo.tickets_sold-tickets_blocked : 0;
		var editing_hold = selectedHold===null ? null : (selectedHold===-1 ? holderAux : holders.find(a=>a.hold_id==selectedHold));
		return {
			tickets_available,
			tickets_blocked,
			editing_hold,
		}
	}, [holders, holderAux, ticketInfo, selectedHold]);


	return (
		<Modal open={props.open} onClose={props.onClose} size='small'>
			<Modal.Header>{props.title || 'Asignar Butaca'}</Modal.Header>
			<Modal.Content>
				{(!holders || loadError) ? (
					<div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<Header loading={!loadError} text={loadError || 'Cargando butacas reservadas...'} iconName={loadError ? 'face-frown-open' : null} size={loadError ? 'small' : null} />
					</div>
				) : <>
					<Stat.Group>
						<Stat>
							<Stat.Value style={{ lineHeight: props.infinite ? 'auto' : '24px' }}>
								{addCommas(ticketInfo.tickets_sold, false)}
								{!props?.infinite && (
									<span style={{ color: 'gray', fontSize: 14 }}>/{ticketInfo.ticket_count}</span>
								)}
							</Stat.Value>
							<Stat.Label text={'Boletos vendidos'} />
						</Stat>
						<Stat value={addCommas(tickets_blocked, false)} label={'Boletos bloqueados'} style={{ marginBottom: 10, marginTop: -10 }} />
						<Stat value={props?.infinite ? <Icon name="infinity" />: tickets_available} label={'Boletos disponibles'} style={{ marginBottom: 10, marginTop: -10 }} />
					</Stat.Group>
					<Table fitted last>
						<Table.Head
							title='Boletos bloqueados'
							data={[['ID', 'Cantidad', 'Comentario', 'Usuario', <Icon name='remove' />, <Icon name='pen' />]]}
							collapsingIndexes={[0, 1, 4, 5]}
							centeredIndexes={[4, 5]}
							titleRight={(
								<Button text='Agregar' color='black' size='tiny' iconName='plus-circle' onClick={onNewHolder} disabled={selectedHold!==null} />
							)}
						/>
						<Table.Body>
							{holders.length === 0 && selectedHold===null && (
								<Table.Cell row empty colSpan={100}>
									Este calendario no tiene boletos bloquedos en esta sección
								</Table.Cell>
							)}
							{holders.map(e=>{
								var editMode = selectedHold!==null;
								var edit = selectedHold === e.hold_id;
								return <Table.Row
									key={`sevt-${e.hold_id}`}
								>
									<Table.Cell collapsing>{e.hold_id || ''}</Table.Cell>
									<Table.Cell collapsing={!editMode} centered>
										{!loading && edit && !!editing_hold ? (
											<Input placeholder="Cantidad" value={editing_hold.amount} onChange={onFormChange('amount')} style={{ margin: 0 }} inputStyle={{ textAlign: 'center' }} />
										) : addCommas(parseInt(e.amount), false)}
									</Table.Cell>
									<Table.Cell>
										{!loading && edit && !!editing_hold ? (
											<Input placeholder="Comentario" value={editing_hold.comments} onChange={onFormChange('comments')} style={{ margin: 0 }} />
										) : e.comments}
									</Table.Cell>
									<Table.Cell>{e.user_creator_name}</Table.Cell>
									<Table.Cell collapsing>
										<Button iconName='remove' color='black' size='tiny' style={{ minWidth: 40 }} disabled={editMode} loading={loading && edit} onClick={onDeleteRow(e.hold_id)} />
									</Table.Cell>
									<Table.Cell collapsing>
										<Button iconName={edit ? 'check' : 'pen'} color='black' size='tiny' style={{ minWidth: 40 }} onClick={edit ? onSaveRow(e.hold_id) : onClickEditRow(e.hold_id)} disabled={!edit} loading={loading && edit}/>
									</Table.Cell>
								</Table.Row>
							})}
							{selectedHold===-1 && !!holderAux && !!editing_hold && (
								<Table.Row data={[
									<Icon name="plus-circle" />,
									<Input
										placeholder="Cantidad"
										value={editing_hold.amount}
										onChange={onFormChange('amount')}
										style={{ width: 100, margin: 0 }}
										inputStyle={{ textAlign: 'center' }}
									/>,
									<Input
										placeholder="Comentario"
										value={editing_hold.comments}
										onChange={onFormChange('comments')}
										style={{ margin: 0 }}
									/>,
									editing_hold.user_creator_name,
									<Button iconName='remove' color='black' size='tiny' style={{ minWidth: 40 }} onClick={bindClick(setSelectedHold, null)} loading={loading} />,
									<Button iconName='check' color='black' size='tiny' style={{ minWidth: 40 }} onClick={onSaveRow(selectedHold)} loading={loading} />
								]} />
							)}
						</Table.Body>
					</Table>
				</>}
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={props.onClose} />
			</Modal.Actions>
		</Modal>
	)
}

export default SectionHoldModal;
